import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { authApi, type LoginPayload } from '../utils/apiClient';

function Login() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError(null);
        setLoading(true);

        const payload: LoginPayload = { email, password };
        const result = await authApi.login(payload);
        setLoading(false);

        if (!result.success) {
            setError(result.error);
            toast.error(result.error);
            return;
        }

        localStorage.setItem('loginResponse', JSON.stringify(result.data));
        toast.success('Welcome back!');
        navigate('/dashboard');
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-emerald-50 to-emerald-100 px-6 py-12">
            <div className="max-w-5xl w-full grid gap-12 md:grid-cols-[1.1fr,0.9fr] items-center rounded-3xl bg-white/90 border border-emerald-200 shadow-2xl shadow-emerald-200/80 overflow-hidden">
                <div className="px-8 py-10 space-y-5">
                    <p className="text-xs uppercase tracking-[0.6em] text-emerald-500">Carewinners Admin</p>
                    <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                        Secure login for the team portal
                    </h1>
                    <p className="text-slate-600">
                        The Carewinners workspace keeps your schedules, communications, and caregiver credentials in one place.
                        Sign in to access the dashboard.
                    </p>
                    <div className="flex flex-wrap gap-3 text-sm text-emerald-700">
                        {['SSO', '2FA', 'Audits'].map((tag) => (
                            <span key={tag} className="px-3 py-1 rounded-full bg-emerald-100">
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>
                <div className="bg-white border-l border-emerald-100 px-8 py-10">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1" htmlFor="email">
                                Email
                            </label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                placeholder="you@carewinners.org"
                                className="w-full rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-slate-900 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1" htmlFor="password">
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                placeholder="Enter password"
                                className="w-full rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-slate-900 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
                                required
                            />
                        </div>
                        <div className="flex items-center justify-between text-sm text-slate-500">
                            <label className="flex items-center gap-2">
                                <input type="checkbox" className="h-4 w-4 rounded border-emerald-300 bg-emerald-100" />
                                Remember me
                            </label>
                            <button type="button" className="text-emerald-600 hover:text-emerald-500 font-semibold">
                                Forgot password?
                            </button>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 rounded-2xl bg-[#78C45E] text-white font-semibold text-sm transition hover:bg-[#78C45E]/90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {loading ? 'Signing in...' : 'Sign in'}
                        </button>
                        {error && (
                            <p className="text-sm text-center text-rose-600" aria-live="assertive">
                                {error}
                            </p>
                        )}
                    </form>
                    <p className="text-center text-xs text-slate-500 mt-6">
                        New to Carewinners Admin?{' '}
                        <Link to="/signup" className="text-emerald-600 underline decoration-emerald-400 hover:text-emerald-500">
                            Create an account
                        </Link>
                        .
                    </p>
                </div>
            </div>
        </div>
    );
}

export default Login;
